import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { CompanyType } from '@planning/app/api'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { containerTypes } from '@planning/constants'
import { useTranslate } from '@tolgee/react'
import {
  CustomInputAdornment,
  useContainerAttributesParameters,
  useMinimalsTheme,
} from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { TrailerCreationFormStore } from '../stores/TrailerCreationFormStore'
import { getUnitNumberErrorText } from './SwapBodyCreationForm'

interface Props {
  store: TrailerCreationFormStore
  isOperatorDisabled?: boolean
}

export const TrailerCreationForm: FC<Props> = observer(({ store, isOperatorDisabled }) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const { types: containerTypeParameters } = useContainerAttributesParameters()

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
      >
        <TextField
          required
          label={t('iluCode', 'ILU Code')}
          value={store.iluCode.value}
          onChange={e => store.iluCode.setValue(e.target.value.toUpperCase())}
          fullWidth
          variant='outlined'
          data-cy='unit-number'
          error={!!store.operator.error}
          helperText={getUnitNumberErrorText(store.iluCode.error!, t)}
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
      >
        <CompanyAutocomplete
          required
          label={t('operator', 'Operator')}
          dataCy='operator-autocomplete'
          disabled={isOperatorDisabled}
          selectedCompanyShortName={store.operator.value}
          handleOnChange={op => store.operator.setValue(op?.shortName ?? '')}
          filterByTypes={[CompanyType.ShippingLine]}
          data-cy='container-operator-field'
          error={!!store.operator.error}
          helperText={store.operator.error ? t('fieldIsRequired', 'Field is required.') : ''}
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
      >
        <TextField
          label={t('length', 'Length')}
          value={store.length.value}
          onChange={e => store.length.setValue(e.target.value.toUpperCase())}
          fullWidth
          variant='outlined'
          data-cy='container-length'
          InputProps={{
            endAdornment: (
              <CustomInputAdornment
                position='end'
                color='secondary'
                padding={theme.customSpacing.l}
              >
                ft
              </CustomInputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid size={6}>
        <FormControl fullWidth>
          <InputLabel>{t('unitType', 'Unit Type')}</InputLabel>
          <Select
            label={`${t('unitType', 'Unit Type')}`}
            value={store.type.value}
            onChange={(e: SelectChangeEvent) => store.type.setValue(e.target.value)}
            data-cy='container-type'
          >
            {containerTypes
              ?.filter(d => containerTypeParameters[d.name]?.label)
              .map(d => (
                <MenuItem key={d.key} value={d.name}>
                  {containerTypeParameters[d.name].label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid
        size={{
          sm: 12,
        }}
      >
        <TextField
          label={t('plateNo', 'Plate no.')}
          value={store.plateNumber.value}
          onChange={e => store.plateNumber.setValue(e.target.value)}
          fullWidth
          variant='outlined'
          data-cy='trailer-plate-no-field'
        />
      </Grid>
    </Grid>
  )
})
